import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../App';
import { t } from '../i18n';
import LanguageToggle from '../components/game/LanguageToggle';
import Sidebar from '../components/game/Sidebar';
import { Check, X, Infinity, School } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../AuthContext';

const PLANS = [
  {
    id: 'free',
    nameKey: 'planFree',
    price: '0',
    currency: 'RM',
    period: '',
    color: '#6b7280',
    features: [
      { key: 'pf1_free', included: true },
      { key: 'pf2_free', included: true },
      { key: 'pf3_free', included: true },
      { key: 'pf4_free', included: true },
      { key: 'pf5_free', included: false },
      { key: 'pf6_free', included: false },
    ],
  },
  {
    id: 'basic',
    nameKey: 'planBasic',
    price: '19.90',
    priceAnnual: '15.90',
    currency: 'RM',
    period: 'month',
    color: '#2196F3',
    features: [
      { key: 'pf1_basic', included: true },
      { key: 'pf2_basic', included: true },
      { key: 'pf3_basic', included: true },
      { key: 'pf4_basic', included: true },
      { key: 'pf5_basic', included: true },
      { key: 'pf6_basic', included: false },
    ],
  },
  {
    id: 'pro',
    nameKey: 'planPro',
    price: '49.90',
    priceAnnual: '39.90',
    currency: 'RM',
    period: 'month',
    color: '#E85D3A',
    popular: true,
    features: [
      { key: 'pf1_pro', included: true },
      { key: 'pf2_pro', included: true },
      { key: 'pf3_pro', included: true },
      { key: 'pf4_pro', included: true },
      { key: 'pf5_pro', included: true },
      { key: 'pf6_pro', included: true },
    ],
  },
  {
    id: 'school',
    nameKey: 'planSchool',
    price: '199',
    priceAnnual: '159',
    currency: 'RM',
    period: 'month',
    color: '#009688',
    features: [
      { key: 'pf1_school', included: true },
      { key: 'pf2_school', included: true },
      { key: 'pf3_school', included: true },
      { key: 'pf4_school', included: true },
      { key: 'pf5_school', included: true },
      { key: 'pf6_school', included: true },
      { key: 'pf7_school', included: true },
    ],
  },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PricingPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user, token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isAnnual, setIsAnnual] = useState(false);
  const [loading, setLoading] = useState(null);
  const currentPlan = user?.plan || null;

  const handleSubscribe = async (planId) => {
    if (!user || !token) { navigate('/login'); return; }
    if (currentPlan === planId) return;
    if (planId === 'free') return;
    if (planId === 'school') {
      window.location.href = 'mailto:imafiqwork@gmail.com?subject=PANTASS%20School%20Plan%20Inquiry&body=Saya%20berminat%20dengan%20pelan%20School%20PANTASS.';
      return;
    }
    setLoading(planId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/stripe/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan: planId, billing: isAnnual ? 'annual' : 'monthly' })
      });
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert(data.detail || 'Error creating checkout');
      }
    } catch (e) {
      console.error(e);
      alert('Failed to create checkout session');
    } finally {
      setLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/stripe/portal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.portal_url) window.location.href = data.portal_url;
    } catch (e) { console.error(e); }
  };

  return (
    <div className="dashboard-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="dashboard-header">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <h1 className="dashboard-header-title">{t('pricing', language)}</h1>
        <LanguageToggle className="dashboard-lang-toggle" />
      </header>

      <div className="dashboard-content">
        <div className="pricing-intro">
          <span className="pricing-label">{t('pricing', language).toUpperCase()}</span>
          <h2 className="pricing-title">{t('pricingTitle', language)}</h2>
          <p className="pricing-desc">{t('pricingDesc', language)}</p>
        </div>

        {/* Billing Toggle */}
        <div className="billing-toggle-wrapper">
          <div className="billing-toggle">
            <button className={`billing-btn ${!isAnnual ? 'billing-btn-active' : ''}`}
              onClick={() => setIsAnnual(false)}>{t('monthly', language)}</button>
            <button className={`billing-btn ${isAnnual ? 'billing-btn-active' : ''}`}
              onClick={() => setIsAnnual(true)}>
              {t('annual', language)}
              <span className="billing-save-badge">{t('save20', language)}</span>
            </button>
          </div>
        </div>

        <div className="pricing-highlights">
          <div className="pricing-highlight-pill">{t('pricingH1', language)}</div>
          <div className="pricing-highlight-pill">{t('pricingH2', language)}</div>
          <div className="pricing-highlight-pill">{t('pricingH3', language)}</div>
        </div>

        <div className="pricing-cards-v2">
          {PLANS.map((plan, index) => {
            const displayPrice = isAnnual && plan.priceAnnual ? plan.priceAnnual : plan.price;
            return (
              <motion.div
                key={plan.id}
                className={`pricing-card-v2 ${plan.popular ? 'pricing-card-popular-v2' : ''} ${plan.id === 'school' ? 'pricing-card-school' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                data-testid={`pricing-card-${plan.id}`}
              >
                {plan.popular && (
                  <div className="pricing-popular-badge-v2">{t('popular', language)}</div>
                )}
                {plan.id === 'school' && (
                  <div className="pricing-school-badge">
                    <School size={14} /> {t('bestValue', language)}
                  </div>
                )}
                <div className="pricing-card-header-v2">
                  <h3 className="pricing-plan-name-v2">{t(plan.nameKey, language)}</h3>
                  {displayPrice === '0' ? (
                    <div className="pricing-price-v2">
                      <span className="pricing-price-amount-v2">{t('planFree', language)}</span>
                    </div>
                  ) : (
                    <div className="pricing-price-v2">
                      <span className="pricing-price-currency-v2">{plan.currency}</span>
                      <span className="pricing-price-amount-v2">{displayPrice}</span>
                      <span className="pricing-price-period-v2">/{t(plan.period, language)}</span>
                    </div>
                  )}
                  {isAnnual && plan.priceAnnual && plan.price !== '0' && (
                    <div className="pricing-original-price">
                      <s>RM{plan.price}</s>
                    </div>
                  )}
                  {plan.id === 'school' && (
                    <div className="pricing-per-student">
                      {t('perStudent', language)}
                    </div>
                  )}
                </div>
                <div className="pricing-divider-v2" />
                <ul className="pricing-features-v2">
                  {plan.features.map((feature, i) => (
                    <li key={i} className={`pricing-feature-v2 ${!feature.included ? 'pricing-feature-disabled' : ''}`}>
                      {feature.included ? (
                        <Check size={16} className="pricing-check" />
                      ) : (
                        <X size={16} className="pricing-x" />
                      )}
                      <span>{t(feature.key, language)}</span>
                    </li>
                  ))}
                </ul>
                <button
                  className={`pricing-cta-v2 ${currentPlan === plan.id ? 'pricing-cta-current' : ''}`}
                  style={{ background: currentPlan === plan.id ? '#6b7280' : plan.color }}
                  onClick={() => currentPlan === plan.id ? handleManageSubscription() : handleSubscribe(plan.id)}
                  disabled={loading === plan.id}
                  data-testid={`pricing-cta-${plan.id}`}
                >
                  {loading === plan.id ? '...' : currentPlan === plan.id ? t('managePlan', language) : plan.price === '0' ? t('startFree', language) : plan.id === 'school' ? t('contactUs', language) : t('subscribe', language)}
                </button>
              </motion.div>
            );
          })}
        </div>

        <button className="setup-back-btn" onClick={() => navigate('/')}>
          &larr; {t('backToHome', language)}
        </button>
      </div>
    </div>
  );
}
